/**
 * @file meetingPaletteUtils.ts
 * @brief Shared Meeting Intel helpers for Cmd+K page/filter/entity hits.
 *
 * @details
 * Keeps synonym coverage and list-row → palette item mapping in one place so
 * typing "meeting intelligence", participant names, owners, or overview text
 * surfaces the right destinations.
 *
 * @author DES
 * @date 30-07-2026
 */

import { BoardroomTable } from "@/components/icons/BoardroomTable";
import type { AdminMeetingRow } from "@/features/admin-meetings/schemas";
import { meetingOwnerLabel } from "@/features/admin-meetings/schemas";
import type { MeetingRow } from "@/features/company-meetings/schemas";
import type { CommandPaletteItem } from "./commandPaletteTypes";

/** Synonyms for the abbreviated "Meeting Intel" nav label. */
export const MEETING_INTEL_SYNONYMS =
  "meeting intelligence intelligence mi mom minutes of meeting boardroom summaries action items speakers diarization analytics";

function peopleNames(
  meeting: Pick<MeetingRow, "attendees" | "participants">,
): string[] {
  const people = meeting.attendees ?? meeting.participants ?? [];
  return people
    .map((p) => p.name?.trim() || p.email?.trim() || "")
    .filter(Boolean);
}

function peopleEmails(
  meeting: Pick<MeetingRow, "attendees" | "participants">,
): string[] {
  const people = meeting.attendees ?? meeting.participants ?? [];
  return people.map((p) => p.email?.trim() || "").filter(Boolean);
}

function todoTasks(meeting: Pick<MeetingRow, "todos">): string[] {
  return (meeting.todos ?? [])
    // Dismissed to-dos are not searchable (todoDismiss.ts).
    .filter((t) => !(t as { dismissedAt?: string | null }).dismissedAt)
    .map((t) => t.task?.trim() || "")
    .filter(Boolean);
}

function decisionTexts(meeting: Pick<MeetingRow, "key_decisions">): string[] {
  return (meeting.key_decisions ?? [])
    .map((d) => (typeof d === "string" ? d.trim() : ""))
    .filter(Boolean);
}

/** Extra keywords for nav page jumps that relate to Meeting Intel / boardroom. */
export function meetingRelatedPageKeywords(to: string): string {
  const path = to.split(/[?#]/)[0] ?? to;
  if (path.endsWith("/meetings")) {
    return MEETING_INTEL_SYNONYMS;
  }
  if (path.includes("/meetings/new")) {
    return `record capture laptop new boardroom meeting ${MEETING_INTEL_SYNONYMS}`;
  }
  if (path.includes("/meetings/scheduled")) {
    return `scheduled my meetings start now boardroom ${MEETING_INTEL_SYNONYMS}`;
  }
  return "";
}

function meetingDescription(
  meeting: MeetingRow | AdminMeetingRow,
  isSuperAdmin: boolean,
): string {
  const parts: string[] = ["Meeting Intel"];
  if (meeting.started_at) {
    parts.push(meeting.started_at.slice(0, 10));
  }
  // Super Admin rows carry metadata only (B2/W4): company and owner, no
  // attendees.
  if (isSuperAdmin) {
    const admin = meeting as AdminMeetingRow;
    if (admin.company_name?.trim()) parts.push(admin.company_name.trim());
    const owner = meetingOwnerLabel(admin);
    if (owner && owner !== "—") parts.push(owner);
    return parts.join(" · ");
  }
  const member = meeting as MeetingRow;
  if (member.organised_by?.trim()) {
    parts.push(`Organised by ${member.organised_by.trim()}`);
  }
  const names = peopleNames(member).slice(0, 3);
  if (names.length > 0) parts.push(names.join(", "));
  return parts.join(" · ");
}

function meetingKeywords(
  meeting: MeetingRow | AdminMeetingRow,
  isSuperAdmin: boolean,
): string {
  // VERBLIZR staff search metadata only, never overview, decisions or
  // to-dos (B2/W4, DES 22-10-2026).
  if (isSuperAdmin) {
    const admin = meeting as AdminMeetingRow;
    return [
      admin.title ?? "",
      admin.id,
      admin.status ?? "",
      admin.company_name ?? "",
      admin.owner_email ?? "",
      admin.owner_first_name ?? "",
      admin.owner_last_name ?? "",
      meetingOwnerLabel(admin),
      MEETING_INTEL_SYNONYMS,
    ]
      .filter(Boolean)
      .join(" ");
  }

  const member = meeting as MeetingRow;
  const bits: string[] = [
    member.title ?? "",
    member.id,
    member.status ?? "",
    member.meeting_overview ?? "",
    member.attention_risks ?? "",
    member.organised_by ?? "",
    member.started_by ?? "",
    ...peopleNames(member),
    ...peopleEmails(member),
    ...decisionTexts(member).slice(0, 8),
    ...todoTasks(member).slice(0, 8),
    MEETING_INTEL_SYNONYMS,
  ];

  return bits.filter(Boolean).join(" ");
}

export function buildMeetingPaletteItem(
  meeting: MeetingRow | AdminMeetingRow,
  options: { isSuperAdmin: boolean; base?: string },
): CommandPaletteItem {
  const { isSuperAdmin } = options;
  const base = options.base ?? (isSuperAdmin ? "/admin/meetings" : "/company/meetings");
  return {
    id: `meeting:${meeting.id}`,
    kind: "meeting",
    label: meeting.title?.trim() || "Untitled meeting",
    description: meetingDescription(meeting, isSuperAdmin),
    keywords: meetingKeywords(meeting, isSuperAdmin),
    icon: BoardroomTable, // DES 08-10-2026: app icon, was Presentation
    to: `${base}/${meeting.id}`,
  };
}
